import { useReducer } from "react";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { downloadSentOtcInvoice } from "@/lib/api/otcInvoices";
import { downloadFromS3 } from "@/lib/file";
import { DownloadSentOtcInvoiceRequestValidation } from "@/lib/validation/otcInvoices";

type DownloadState = {
  isOpen: boolean;
  status: "idle" | "generating" | "success" | "error";
  url: string | null;
  error: string | null;
};

type DownloadAction =
  | { type: "generate" }
  | { type: "success"; url: string }
  | { type: "error"; error: string }
  | { type: "close" };

const initialDownloadState: DownloadState = {
  isOpen: false,
  status: "idle",
  url: null,
  error: null,
};

function downloadReducer(state: DownloadState, action: DownloadAction): DownloadState {
  switch (action.type) {
    case "generate":
      return { isOpen: true, status: "generating", url: null, error: null };
    case "success":
      return { ...state, status: "success", url: action.url, error: null };
    case "error":
      return { ...state, status: "error", url: null, error: action.error };
    case "close":
      return initialDownloadState;
  }
}

function errorDescription(result: { message?: string; data?: unknown }): string {
  const context = result.data === undefined ? "" : ` ${JSON.stringify(result.data)}`;
  return `${result.message || "The request failed."}${context}`;
}

export function DownloadOtcInvoiceButton({ invoiceId }: { invoiceId: string }) {
  const [state, dispatch] = useReducer(downloadReducer, initialDownloadState);

  const runDownload = async (): Promise<void> => {
    const parsed = DownloadSentOtcInvoiceRequestValidation.safeParse({ invoice_id: invoiceId });
    if (!parsed.success || state.status === "generating") return;

    dispatch({ type: "generate" });
    try {
      const result = await downloadSentOtcInvoice(parsed.data);
      if (!result.success) {
        dispatch({ type: "error", error: errorDescription(result) });
        return;
      }

      const downloadResult = await downloadFromS3(
        result.data.presigned_get_url,
        result.data.filename,
      );
      if (!downloadResult.success) {
        dispatch({ type: "error", error: "Failed to download the OTC invoice file." });
        return;
      }

      dispatch({ type: "success", url: result.data.presigned_get_url });
    } catch (error: unknown) {
      dispatch({
        type: "error",
        error: error instanceof Error ? error.message : "The request failed.",
      });
    }
  };

  return (
    <>
      <Button type="button" size="sm" variant="outline" onClick={runDownload}>
        <Download className="mr-2 h-4 w-4" /> Download
      </Button>
      <Dialog open={state.isOpen} onOpenChange={(open: boolean) => { if (!open) dispatch({ type: "close" }); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Download OTC Invoice</DialogTitle>
            <DialogDescription>
              {state.status === "generating"
                ? "Generating..."
                : state.status === "error"
                  ? "The invoice download could not be generated."
                  : "Your invoice download is ready."}
            </DialogDescription>
          </DialogHeader>
          {state.error ? <p className="text-sm text-destructive">{state.error}</p> : null}
          {state.url ? (
            <a href={state.url} download className="text-sm text-blue-600 underline">
              If no download has started, right click and save as to download the report
            </a>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => dispatch({ type: "close" })}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
